import { effectAsParsed, effectRulesDefaults } from '../effects/defaults';
import { findModifierKey, type ModifierDef } from '../effects/types';
import {
  awardsDefaults,
  backgroundDefaults,
  challengesDefaults,
  coreStatDefaults,
  milestoneDefaults,
  perkDefaults,
  rustClassDefaults,
  rustClassIds,
  rustClassOf,
  skillDefaults,
  skillRulesDefaults,
  staminaDefaults,
  startDefaults,
  statAwardsDefaults,
  workoutWeightsDefaults,
} from '../skills/defaults';
import {
  ACTIVITY_CATEGORIES,
  CORE_STAT_IDS,
  ALL_DERIVED_FAMILIES,
  DERIVED_FAMILIES,
  LEGACY_STAT_IDS,
  MILESTONE_ABILITY_AT,
  MILESTONE_PERK_AT,
  MILESTONE_SIGNATURE_AT,
  PERK_FLAGS,
  SHEET_POINTS_MAX,
  SKILL_FAMILIES,
  SKILL_IDS,
  derivedFamilyOf,
  parseOwner,
  skillFamilyOf,
  type GovernDef,
  type MilestoneDef,
  type PerkDef,
  type SkillRulesDef,
  type StaminaDef,
} from '../skills/types';
import { withDefaults } from '../teamsDefaults';
import { isBlank, num, type Err } from './common';
import { sectionEntries } from './sections';
import type { Bundle, BundleKey } from './types';

/*
 * The Outta-Ten sections (ADR 0066, CONTRACT §2): a port of packages/shared-cs/Runtime/Content/ContentValidator.Skills.cs with the
 * same messages in the same order. Change both together; parity is pinned by validate/validate.skills.test.ts against
 * fixtures/ot-parity.json (dumped by packages/shared-cs/Tests~/OtAdmParityDump.cs).
 * - Rows (coreStats[], skills[]): the six stats and every launch skill once, lower-case ids, known families, baseline 0..10, learnSpeed
 *   0.1..3, a known rustClass; governs on registered non-level keys once each, a table of exactly 11 whose pivot entry is the key's
 *   neutral (the parity rule); milestone modifiers on registered keys with the key's op, known flags, and no numbers on a milestone a
 *   baseline sheet already has; ability7 required, naming a perk the row owns and no other row's ability7 (CONTRACT §13.4: the perk
 *   simply arrives at 7, no picks), without modifiers when a baseline sheet already has it; affectsText and trainText required,
 *   20..240 characters, without the retired stat names (CONTRACT §13.2).
 * - perks[]: unique ids, an owner row that exists, a name, a modifier or a flag. A perk no row names is allowed (kept for later).
 * - skillRules: the ranges, known condition ids, devPointsMult 1..10 (1 in production: `skillsProductionProblems`).
 * - raidRules.stamina; every weapon (and raidRules.unarmed) names a weapon skill once the bundle has skills[]; zombie and boss
 *   challenge 1..10; the reserved requiresSkill on recipes and module levels.
 * - backgrounds[] bias keys `stat:<id>` / `skill:<id>`; effectRules.derivedFamilies; the old ADR 0044 stat ids are errors.
 * Absent sections and fields are their C# defaults; an explicit null section is "Bundle is missing".
 */

const SKILL_ID = /^[a-z][a-z0-9_]*$/;
const isShare = (v: number) => v >= 0 && v <= 1;
const isFinite = (v: number | null | undefined) => typeof v === 'number' && Number.isFinite(v);
const isStat = (id: string | null | undefined) =>
  (CORE_STAT_IDS as readonly string[]).includes(id ?? '');
const isFlag = (f: string | null | undefined) =>
  (PERK_FLAGS as readonly string[]).includes(f ?? '');

/** `LegacyHint`: " (ADR 0066: brawn is now str)" for an old ADR 0044 stat id, else "". */
export const legacyHint = (id: string | null | undefined) =>
  id != null && id in LEGACY_STAT_IDS ? ` (ADR 0066: ${id} is now ${LEGACY_STAT_IDS[id]})` : '';
/** `LegacyKeyHint`: the hint for `stat.<old id>`. */
export const legacyKeyHint = (key: string | null | undefined) =>
  key != null && key.startsWith('stat.') ? legacyHint(key.slice('stat.'.length)) : '';
const isOldStat = (id: string | null | undefined) =>
  id != null && !isStat(id) && id in LEGACY_STAT_IDS;

/** `skillRules` as the server parses it: the C# initializers under every absent field and nested block. */
export function skillRulesAsParsed(raw: SkillRulesDef): SkillRulesDef {
  const r = withDefaults(skillRulesDefaults(), raw);
  const awards = r.awards == null ? r.awards : withDefaults(awardsDefaults(), r.awards);
  if (awards?.stat != null) awards.stat = withDefaults(statAwardsDefaults(), awards.stat);
  return {
    ...r,
    awards,
    challenges:
      r.challenges == null ? r.challenges : withDefaults(challengesDefaults(), r.challenges),
    workouts: r.workouts == null ? r.workouts : withDefaults(workoutWeightsDefaults(), r.workouts),
    start: r.start == null ? r.start : withDefaults(startDefaults(), r.start),
    rustClasses:
      r.rustClasses == null
        ? r.rustClasses
        : Object.fromEntries(
            Object.entries(r.rustClasses).map(([k, c]) => [
              k,
              c == null ? c : withDefaults(rustClassDefaults(), c),
            ]),
          ),
  };
}

type Rows = { owners: Map<string, number>; ability7Of: Map<string, string> };
type Add = (section: BundleKey, index: number | undefined, path: string, message: string) => void;

export function validateSkills(b: Bundle, err: Err) {
  const add: Add = err;
  const stats = sectionEntries('coreStats', b.coreStats, err);
  const skills = sectionEntries('skills', b.skills, err);
  const perks = sectionEntries('perks', b.perks, err);
  const backgrounds = sectionEntries('backgrounds', b.backgrounds, err);
  if (b.skillRules === null) err('skillRules', undefined, '', "Bundle is missing 'skillRules'.");
  const rules = skillRulesAsParsed(b.skillRules ?? {});

  const rustClasses = new Set(rustClassIds(rules));
  const perkById = new Map<string, PerkDef>();
  for (const [raw, i] of perks) {
    const p = withDefaults(perkDefaults(), raw);
    const id = p.id ?? '';
    if (id === '' || !SKILL_ID.test(id))
      add('perks', i, 'id', `Perk '${id}' needs a lower-case id (letters, digits, _).`);
    else if (perkById.has(id)) add('perks', i, 'id', `Duplicate perk id '${id}'.`);
    else perkById.set(id, p);
  }

  const rows: Rows = { owners: new Map(), ability7Of: new Map() };
  const weaponSkills = new Set<string>();
  const allSkills = new Set<string>();
  for (const [raw, i] of stats) {
    const s = withDefaults(coreStatDefaults(), raw);
    const id = s.id ?? '';
    const w = `Core stat '${id}'`;
    const fail = (path: string, m: string) => add('coreStats', i, path, m);
    if (!isStat(id)) {
      fail('id', `${w} isn't a stat (${CORE_STAT_IDS.join(', ')})${legacyHint(id)}.`);
      continue;
    }
    const owner = `stat:${id}`;
    if (rows.owners.has(owner)) {
      fail('id', `Duplicate core stat id '${id}'.`);
      continue;
    }
    rows.owners.set(owner, 5);
    if (isBlank(s.name)) fail('name', `${w} needs a name.`);
    if (isBlank(s.short)) fail('short', `${w} needs a short name.`);
    row(w, owner, 5, s, rustClasses, perkById, rows, fail);
    infoTexts(w, s, fail);
  }
  if (stats.length > 0)
    for (const id of CORE_STAT_IDS)
      if (!rows.owners.has(`stat:${id}`))
        add('coreStats', undefined, '', `coreStats is missing '${id}'.`);

  for (const [raw, i] of skills) {
    const s = withDefaults(skillDefaults(), raw);
    const id = s.id ?? '';
    const w = `Skill '${id}'`;
    const fail = (path: string, m: string) => add('skills', i, path, m);
    if (id === '' || !SKILL_ID.test(id)) {
      fail('id', `${w} needs a lower-case id (letters, digits, _).`);
      continue;
    }
    if (isStat(id)) fail('id', `${w} uses a stat id.`);
    const owner = `skill:${id}`;
    if (rows.owners.has(owner)) {
      fail('id', `Duplicate skill id '${id}'.`);
      continue;
    }
    const baseline = num(s.baseline);
    rows.owners.set(owner, baseline);
    allSkills.add(id);
    if (!(SKILL_FAMILIES as readonly string[]).includes(s.family ?? ''))
      fail('family', `${w} needs family weapon, field or home.`);
    else if (s.family === 'weapon') weaponSkills.add(id);
    const family = skillFamilyOf(id);
    if (family != null && family !== s.family)
      fail('family', `${w} must be in family '${family}'.`);
    if (isBlank(s.name)) fail('name', `${w} needs a name.`);
    if (baseline < 0 || baseline > 10) fail('baseline', `${w} needs baseline in 0..10.`);
    const cantBelow = num(s.cantBelow);
    if (cantBelow < 0 || cantBelow > 10) fail('cantBelow', `${w} needs cantBelow in 0..10.`);
    row(w, owner, baseline, s, rustClasses, perkById, rows, fail);
    infoTexts(w, s, fail);
  }
  if (skills.length > 0)
    for (const id of SKILL_IDS)
      if (!allSkills.has(id)) add('skills', undefined, '', `skills is missing '${id}'.`);

  for (const [raw, i] of perks) {
    const p = withDefaults(perkDefaults(), raw);
    const id = p.id ?? '';
    if (id === '') continue;
    const w = `Perk '${id}'`;
    const fail = (path: string, m: string) => add('perks', i, path, m);
    const owner = p.owner ?? '';
    if (parseOwner(owner) == null) fail('owner', `${w} needs owner stat:<id> or skill:<id>.`);
    else if (!rows.owners.has(owner)) fail('owner', `${w} has unknown owner '${owner}'.`);
    if (isBlank(p.name)) fail('name', `${w} needs a name.`);
    if ((p.modifiers ?? []).length === 0 && (p.flags ?? []).length === 0)
      fail('modifiers', `${w} needs a modifier or a flag.`);
    modifiers(w, p.modifiers, (m) => fail('modifiers', m));
    flags(w, p.flags, (m) => fail('flags', m));
  }

  skillRulesSection(rules, b, allSkills, weaponSkills, (path, m) =>
    add('skillRules', undefined, path, m),
  );
  stamina(b.raidRules === undefined ? staminaDefaults() : b.raidRules?.stamina, (path, m) =>
    add('raidRules', undefined, path, m),
  );
  weaponSkillsCheck(b, weaponSkills, skills.length > 0, add);
  challenges(b, add);
  requirements(b, allSkills, add);
  backgroundsCheck(backgrounds, rows.owners, add);
  derivedFamilies(b, skills.length > 0 || stats.length > 0, add);
  oldStatIds(b, add);
}

type Fail = (path: string, message: string) => void;
type Row = {
  learnSpeed?: number;
  rustClass?: string;
  governs?: (GovernDef | null)[] | null;
  ability5?: MilestoneDef | null;
  ability7?: string | null;
  signature10?: MilestoneDef | null;
};

function row(
  w: string,
  owner: string,
  pivot: number,
  s: Row,
  rustClasses: Set<string>,
  perks: Map<string, PerkDef>,
  rows: Rows,
  fail: Fail,
) {
  const learn = num(s.learnSpeed);
  if (!(learn >= 0.1 && learn <= 3)) fail('learnSpeed', `${w} needs learnSpeed in 0.1..3.`);
  if (!rustClasses.has(s.rustClass ?? ''))
    fail('rustClass', `${w} has unknown rustClass '${s.rustClass ?? ''}'.`);

  const keys = new Set<string>();
  (s.governs ?? []).forEach((raw, j) => {
    if (raw == null) return;
    const g = withDefaults({ key: '', perLevel: 0, table: [] as number[] }, raw);
    const at = `governs.${j}`;
    const key = g.key ?? '';
    const gw = `${w} governs '${key}'`;
    const k = findModifierKey(key);
    if (k == null) {
      fail(at, `${w} governs unknown key '${key}'.`);
      return;
    }
    if (key.startsWith('stat.') || key.startsWith('skill.'))
      fail(at, `${gw}: a stat or skill can't govern a level key.`);
    if (keys.has(key)) fail(at, `${w} governs '${key}' twice (one contribution per key).`);
    keys.add(key);
    if (!isFinite(g.perLevel ?? 0)) fail(at, `${gw} needs a finite perLevel.`);
    const table = g.table ?? [];
    if (table.length === 0) return;
    if (table.length !== 11) {
      fail(at, `${gw} needs a table of 11 values (levels 0..10).`);
      return;
    }
    const ok = table.every((v) => isFinite(v) && (k.op !== 'mult' || v >= 0));
    if (!ok) fail(at, `${gw} table needs finite values${k.op === 'mult' ? ' of 0 or more.' : '.'}`);
    if (pivot >= 0 && pivot <= 10 && Math.abs(num(table[pivot]) - k.neutral) > 1e-6)
      fail(
        at,
        `${gw} table must be ${k.neutral} at the pivot (level ${pivot}), so a baseline sheet plays like today.`,
      );
  });

  milestone(`${w} ability5`, s.ability5, pivot >= MILESTONE_ABILITY_AT, (m) => fail('ability5', m));
  milestone(`${w} signature10`, s.signature10, pivot >= MILESTONE_SIGNATURE_AT, (m) =>
    fail('signature10', m),
  );

  // The perk at 7 (CONTRACT §13.4): one perk the row owns, arriving at 7 with no choice.
  const a7 = s.ability7 ?? '';
  if (a7 === '') {
    fail('ability7', `${w} needs ability7 (a perk it owns, which arrives at 7).`);
    return;
  }
  const p7 = perks.get(a7);
  if (p7 === undefined) fail('ability7', `${w} ability7 names unknown perk '${a7}'.`);
  else if (p7.owner !== owner)
    fail('ability7', `${w} ability7 names perk '${a7}', owned by '${p7.owner ?? ''}'.`);
  else if (rows.ability7Of.has(a7)) fail('ability7', `Perk '${a7}' is the ability7 of two rows.`);
  else {
    rows.ability7Of.set(a7, owner);
    if (pivot >= MILESTONE_PERK_AT && (p7.modifiers ?? []).length > 0)
      fail(
        'ability7',
        `${w} ability7 '${a7}' is live on a baseline sheet, so it can't carry modifiers (flags and text only).`,
      );
  }
}

/** `ContentValidator.InfoTextMin` / `InfoTextMax` (CONTRACT §13.2: one or two short sentences). */
export const INFO_TEXT_MIN = 20;
export const INFO_TEXT_MAX = 240;
/**
 * `RetiredStatWords`: the ADR 0044 stat names as whole words, BRAWN / AIM / CARE case-sensitive and "brawn" in any case. (JS `\b` is
 * ASCII-only where .NET's is Unicode; the words are ASCII, so only a non-ASCII letter glued to one could differ.)
 */
const RETIRED_STAT_WORDS = /\b(BRAWN|AIM|CARE)\b|\b[Bb][Rr][Aa][Ww][Nn]\b/;

function infoTexts(
  w: string,
  s: { affectsText?: string | null; trainText?: string | null },
  fail: Fail,
) {
  infoText(`${w} affectsText`, s.affectsText, (m) => fail('affectsText', m));
  infoText(`${w} trainText`, s.trainText, (m) => fail('trainText', m));
}

/** The info-row problems for one text, `[]` when it's fine (the form's live hint uses it too). */
export function infoTextProblems(w: string, text: string | null | undefined): string[] {
  const t = text ?? '';
  if (t.trim().length === 0) return [`${w} is required (what the player reads in the info row).`];
  const out: string[] = [];
  if (t.length < INFO_TEXT_MIN || t.length > INFO_TEXT_MAX)
    out.push(`${w} must be ${INFO_TEXT_MIN}..${INFO_TEXT_MAX} characters (it has ${t.length}).`);
  const m = RETIRED_STAT_WORDS.exec(t);
  if (m) out.push(`${w} names the retired stat '${m[0]}' (ADR 0066).`);
  return out;
}

function infoText(w: string, text: string | null | undefined, fail: (m: string) => void) {
  for (const m of infoTextProblems(w, text)) fail(m);
}

function milestone(
  w: string,
  raw: MilestoneDef | null | undefined,
  atBaseline: boolean,
  fail: (m: string) => void,
) {
  if (raw == null) return;
  const m = withDefaults(milestoneDefaults(), raw);
  if (isBlank(m.name)) fail(`${w} needs a name.`);
  if (atBaseline && (m.modifiers ?? []).length > 0)
    fail(`${w} is live on a baseline sheet, so it can't carry modifiers (flags and text only).`);
  modifiers(w, m.modifiers, fail);
  flags(w, m.flags, fail);
}

function modifiers(
  w: string,
  mods: (ModifierDef | null)[] | null | undefined,
  fail: (m: string) => void,
) {
  for (const raw of mods ?? []) {
    if (raw == null) continue;
    const m = withDefaults({ key: '', op: 'add', value: 0 }, raw);
    const key = m.key ?? '';
    const k = findModifierKey(key);
    if (k == null) fail(`${w} has unknown modifier key '${key}'${legacyKeyHint(key)}.`);
    else if (m.op !== k.op) fail(`${w} modifier '${key}' needs op '${k.op}'.`);
    if (!isFinite(m.value) || (m.op === 'mult' && !(num(m.value) > 0)))
      fail(`${w} modifier '${key}' needs a finite value (above 0 for mult).`);
  }
}

function flags(w: string, list: string[] | null | undefined, fail: (m: string) => void) {
  for (const f of list ?? []) if (!isFlag(f)) fail(`${w} has unknown flag '${f ?? ''}'.`);
}

function skillRulesSection(
  r: SkillRulesDef,
  b: Bundle,
  skills: Set<string>,
  weaponSkills: Set<string>,
  fail: Fail,
) {
  const w = 'skillRules';
  const rate = r.rate ?? [];
  if (rate.length > 0) {
    if (rate.length !== 10) fail('rate', `${w} rate needs 10 values (levels 0..9).`);
    if (rate.some((v) => !(num(v) > 0 && num(v) <= 3)))
      fail('rate', `${w} rate values must be above 0 and at most 3.`);
  }
  const intIn = (v: number | undefined, lo: number, hi: number) => num(v) >= lo && num(v) <= hi;
  if (!intIn(r.dailyCapPoints, 1, SHEET_POINTS_MAX))
    fail('dailyCapPoints', `${w} dailyCapPoints must be in 1..1000.`);
  if (!intIn(r.homeDailyCapPoints, 1, SHEET_POINTS_MAX))
    fail('homeDailyCapPoints', `${w} homeDailyCapPoints must be in 1..1000.`);
  if (!isShare(num(r.challengeFloor)))
    fail('challengeFloor', `${w} challengeFloor must be in 0..1.`);
  if (!isShare(num(r.challengeStepPerLevel)))
    fail('challengeStepPerLevel', `${w} challengeStepPerLevel must be in 0..1.`);
  if (!intIn(r.muscleMemoryMult, 1, 10))
    fail('muscleMemoryMult', `${w} muscleMemoryMult must be in 1..10.`);
  if (!intIn(r.graceDays, 0, 30)) fail('graceDays', `${w} graceDays must be in 0..30.`);
  if (!intIn(r.deathRustDays, 0, 60)) fail('deathRustDays', `${w} deathRustDays must be in 0..60.`);
  for (const id of rustClassIds(r)) {
    const c = rustClassOf(r, id);
    const cw = `${w} rustClasses.${id}`;
    const at = `rustClasses.${id}`;
    if (c == null) continue;
    const perDay = c.perDay ?? [];
    if (c.perDay == null || perDay.length !== 11)
      fail(`${at}.perDay`, `${cw} perDay needs 11 values (levels 0..10).`);
    else if (perDay.some((v) => !(num(v) >= 0 && num(v) <= 100)))
      fail(`${at}.perDay`, `${cw} perDay values must be in 0..100.`);
    if (!intIn(c.floorPoints, 0, SHEET_POINTS_MAX))
      fail(`${at}.floorPoints`, `${cw} floorPoints must be in 0..1000.`);
    if (!intIn(c.belowPeakPoints, 0, SHEET_POINTS_MAX))
      fail(`${at}.belowPeakPoints`, `${cw} belowPeakPoints must be in 0..1000.`);
  }

  const a = r.awards ?? awardsDefaults();
  const awards = [
    a.kill,
    a.headshot,
    a.stealthKill,
    a.search,
    a.forcedDoor,
    a.lockPicked,
    a.treat,
    a.revive,
    a.throwLanded,
    a.bigWin,
    a.homeBuild,
    a.homeDischarge,
  ];
  if (awards.some((v) => !intIn(v, 0, 1000))) fail('awards', `${w} awards must be in 0..1000.`);
  const st = a.stat ?? statAwardsDefaults();
  const statAwards = [
    st.meleeKillStr,
    st.haulStr,
    st.extractedEnd,
    st.waveEnd,
    st.rangedKillRef,
    st.stealthKillRef,
    st.reviveRef,
    st.searchWit,
    st.buildWit,
    st.waveNrv,
    st.bigWinNrv,
    st.revivedNrv,
    st.recruitCha,
    st.tradeCha,
  ];
  if (statAwards.some((v) => !intIn(v, 0, 1000)))
    fail('awards.stat', `${w} awards.stat must be in 0..1000.`);
  if (!(num(st.haulKgPerMember) >= 0))
    fail('awards.stat.haulKgPerMember', `${w} awards.stat.haulKgPerMember must be 0 or more.`);
  const ch = r.challenges ?? challengesDefaults();
  if ([ch.door, ch.lock, ch.revive, ch.treat, ch.search].some((v) => !intIn(v, 1, 10)))
    fail('challenges', `${w} challenges must be in 1..10.`);

  const wk = r.workouts ?? workoutWeightsDefaults();
  weights(
    `${w} workouts.endPerCreditedMin`,
    'workouts.endPerCreditedMin',
    wk.endPerCreditedMin,
    fail,
  );
  weights(
    `${w} workouts.strPerCreditedMin`,
    'workouts.strPerCreditedMin',
    wk.strPerCreditedMin,
    fail,
  );
  weights(
    `${w} workouts.meleePerCreditedMin`,
    'workouts.meleePerCreditedMin',
    wk.meleePerCreditedMin,
    fail,
  );
  for (const id of wk.meleeSkills ?? []) {
    const weapon = skills.size > 0 ? weaponSkills.has(id) : skillFamilyOf(id) === 'weapon';
    if (!weapon)
      fail(
        'workouts.meleeSkills',
        `${w} workouts.meleeSkills names '${id}', which isn't a weapon skill.`,
      );
  }
  if (!intIn(wk.meleeMaxLevel, 0, 10))
    fail('workouts.meleeMaxLevel', `${w} workouts.meleeMaxLevel must be in 0..10.`);
  const sd = r.start ?? startDefaults();
  const six = CORE_STAT_IDS.length;
  if (!intIn(sd.playerSwaps, 0, six) || !intIn(sd.recruitSwaps, 0, six))
    fail('start', `${w} start swaps must be in 0..${six}.`);
  if (!intIn(sd.swapPoints, 0, 500))
    fail('start.swapPoints', `${w} start.swapPoints must be in 0..500.`);
  if (!(num(r.homeSkillLearnPerWit) >= 0 && num(r.homeSkillLearnPerWit) <= 1))
    fail('homeSkillLearnPerWit', `${w} homeSkillLearnPerWit must be in 0..1.`);
  if (!isShare(num(r.reducedShare))) fail('reducedShare', `${w} reducedShare must be in 0..1.`);
  const conditions = new Set(
    (b.conditions?.types ?? []).filter((c) => c != null).map((c) => c!.id ?? ''),
  );
  for (const id of r.reducedConditions ?? [])
    if (!conditions.has(id))
      fail('reducedConditions', `${w} reducedConditions names unknown condition '${id}'.`);
  for (const id of r.noShareConditions ?? [])
    if (!conditions.has(id))
      fail('noShareConditions', `${w} noShareConditions names unknown condition '${id}'.`);
  if (!intIn(r.devPointsMult, 1, 10)) fail('devPointsMult', `${w} devPointsMult must be in 1..10.`);
  if (!isShare(num(r.signatureHeadshot)))
    fail('signatureHeadshot', `${w} signatureHeadshot must be in 0..1.`);
  if (!(num(r.reloadMinS) >= 0 && num(r.reloadMinS) <= 5))
    fail('reloadMinS', `${w} reloadMinS must be in 0..5.`);
  if (!(num(r.knockbackMaxM) > 0 && num(r.knockbackMaxM) <= 10))
    fail('knockbackMaxM', `${w} knockbackMaxM must be above 0 and at most 10.`);
  if (!intIn(r.maxBonusLootRolls, 0, 5))
    fail('maxBonusLootRolls', `${w} maxBonusLootRolls must be in 0..5.`);
}

function weights(
  w: string,
  path: string,
  map: Record<string, number> | null | undefined,
  fail: Fail,
) {
  for (const [k, v] of Object.entries(map ?? {})) {
    if (!(ACTIVITY_CATEGORIES as readonly string[]).includes(k))
      fail(path, `${w} names unknown category '${k}'.`);
    if (!(num(v) >= 0 && num(v) <= 10)) fail(path, `${w}.${k} must be in 0..10.`);
  }
}

function stamina(raw: StaminaDef | null | undefined, fail: Fail) {
  if (raw == null) return;
  const s = withDefaults(staminaDefaults(), raw);
  const w = 'raidRules.stamina';
  const between = (v: number | undefined, lo: number, hi: number) => num(v) >= lo && num(v) <= hi;
  if (!between(s.baseS, 1, 120)) fail('stamina.baseS', `${w} baseS must be in 1..120.`);
  if (!(num(s.regenPerS) > 0 && num(s.regenPerS) <= 10))
    fail('stamina.regenPerS', `${w} regenPerS must be above 0 and at most 10.`);
  if (!between(s.windedS, 0, 10)) fail('stamina.windedS', `${w} windedS must be in 0..10.`);
  if (!between(s.windedSpeedMult, 0.1, 1))
    fail('stamina.windedSpeedMult', `${w} windedSpeedMult must be in 0.1..1.`);
  if (!between(s.windedSpreadMult, 1, 3))
    fail('stamina.windedSpreadMult', `${w} windedSpreadMult must be in 1..3.`);
  if (!between(s.windedAcquireMult, 1, 3))
    fail('stamina.windedAcquireMult', `${w} windedAcquireMult must be in 1..3.`);
}

function weaponSkillsCheck(b: Bundle, weaponSkills: Set<string>, haveSkills: boolean, add: Add) {
  // A bundle without skills[] (published before ADR 0066, or a test fixture) has nothing to check its weapons against.
  if (!haveSkills) return;
  const ok = (id: string | null | undefined) => !!id && weaponSkills.has(id);
  (b.items ?? []).forEach((i, n) => {
    if (i?.weapon == null) return;
    const id = i.weapon.skillId ?? '';
    if (!ok(id))
      add(
        'items',
        n,
        'weapon.skillId',
        `Weapon '${i.id ?? ''}' needs a weapon skill (skillId '${id}').`,
      );
  });
  const unarmed = b.raidRules?.unarmed;
  if (b.raidRules != null && unarmed != null && !ok(unarmed.skillId ?? ''))
    add(
      'raidRules',
      undefined,
      'unarmed.skillId',
      `raidRules.unarmed needs a weapon skill (skillId '${unarmed.skillId ?? ''}').`,
    );
}

function challenges(b: Bundle, add: Add) {
  (b.zombies ?? []).forEach((z, i) => {
    if (z == null) return;
    const c = z.challenge ?? 5;
    if (c < 1 || c > 10)
      add('zombies', i, 'challenge', `Zombie '${z.id ?? ''}' needs challenge in 1..10.`);
  });
  (b.bosses ?? []).forEach((x, i) => {
    if (x == null) return;
    const c = x.challenge ?? 10;
    if (c < 1 || c > 10)
      add('bosses', i, 'challenge', `Boss '${x.id ?? ''}' needs challenge in 1..10.`);
  });
}

function requirements(b: Bundle, skills: Set<string>, add: Add) {
  const check = (
    w: string,
    r: { id?: string; level?: number } | null | undefined,
    fail: (m: string) => void,
  ) => {
    if (r == null) return;
    const id = r.id ?? '';
    if (!(skills.size > 0 ? skills.has(id) : SKILL_IDS.includes(id)))
      fail(`${w} requiresSkill names unknown skill '${id}'.`);
    const level = num(r.level);
    if (level < 1 || level > 10) fail(`${w} requiresSkill needs level in 1..10.`);
  };
  (b.recipes ?? []).forEach((r, i) => {
    if (r == null) return;
    check(`Recipe '${r.id ?? ''}'`, r.requiresSkill, (m) => add('recipes', i, 'requiresSkill', m));
  });
  (b.baseModules ?? []).forEach((m, i) => {
    (m?.levels ?? []).forEach((l, j) => {
      if (l == null) return;
      check(`Module '${m!.id ?? ''}' level ${num(l.level)}`, l.requiresSkill, (msg) =>
        add('baseModules', i, `levels.${j}.requiresSkill`, msg),
      );
    });
  });
}

function backgroundsCheck(
  list: [import('../skills/types').BackgroundDef, number][],
  owners: Map<string, number>,
  add: Add,
) {
  const ids = new Set<string>();
  const roles = new Set<string>();
  let weight = 0;
  for (const [raw, i] of list) {
    const g = withDefaults(backgroundDefaults(), raw);
    const id = g.id ?? '';
    const w = `Background '${id}'`;
    const fail = (path: string, m: string) => add('backgrounds', i, path, m);
    if (id === '') fail('id', 'A background needs an id.');
    else if (ids.has(id)) fail('id', `Duplicate background id '${id}'.`);
    else ids.add(id);
    const gw = num(g.weight);
    if (gw < 0) fail('weight', `${w} needs weight 0 or more.`);
    weight += Math.max(0, gw);
    const legacy = g.legacyRole ?? '';
    if (legacy !== '') {
      if (roles.has(legacy)) fail('legacyRole', `${w} repeats legacyRole '${legacy}'.`);
      else roles.add(legacy);
    }
    for (const [k, v] of Object.entries(g.bias ?? {})) {
      const parsed = parseOwner(k);
      if (parsed == null) {
        fail('bias', `${w} bias key '${k}' must be stat:<id> or skill:<id>${legacyHint(k)}.`);
        continue;
      }
      const [kind, bid] = parsed;
      const pivot = owners.get(k);
      if (pivot === undefined) {
        fail('bias', `${w} biases unknown ${kind} '${bid}'${legacyHint(bid)}.`);
        continue;
      }
      const n = num(v);
      if (kind === 'stat' && (n < -3 || n > 3)) fail('bias', `${w} bias on ${k} must be in -3..3.`);
      if (kind === 'skill' && (n < 0 || n > 5)) fail('bias', `${w} bias on ${k} must be in 0..5.`);
      const start = pivot + n;
      if (start < 0 || start > 10) fail('bias', `${w} puts ${k} at ${start} (must be 0..10).`);
    }
  }
  if (list.length > 0 && weight === 0)
    add('backgrounds', undefined, '', 'Backgrounds need at least one weight above 0.');
}

function derivedFamilies(b: Bundle, required: boolean, add: Add) {
  const rules = b.effectRules ?? effectRulesDefaults();
  const families = rules.derivedFamilies ?? {};
  const fail = (path: string, m: string) => add('effectRules', undefined, path, m);
  const effects = (b.statusEffects ?? []).filter((e) => e != null).map((e) => effectAsParsed(e!));
  for (const [k, v] of Object.entries(families)) {
    const w = `effectRules.derivedFamilies.${k}`;
    const at = `derivedFamilies.${k}`;
    if (!(ALL_DERIVED_FAMILIES as readonly string[]).includes(k))
      fail(at, `${w} isn't a family (${ALL_DERIVED_FAMILIES.join(', ')}).`);
    const e = effects.find((x) => x.id === v);
    if (e === undefined) {
      fail(at, `${w} names unknown effect '${v ?? ''}'.`);
      continue;
    }
    if (!e.hidden) fail(at, `${w} effect '${v}' must be hidden.`);
    if (e.duration?.kind !== 'derived')
      fail(at, `${w} effect '${v}' needs duration.kind 'derived'.`);
    if (e.derived != null) fail(at, `${w} effect '${v}' can't carry a home-skill formula.`);
  }
  if (required)
    for (const f of DERIVED_FAMILIES)
      if (!(f in families)) fail('derivedFamilies', `effectRules.derivedFamilies needs '${f}'.`);
  (b.statusEffects ?? []).forEach((e, i) => {
    const family = e == null ? null : derivedFamilyOf(e.id);
    if (family != null)
      add(
        'statusEffects',
        i,
        'id',
        `Effect id '${e!.id}' is reserved for derived ${family} effects.`,
      );
  });
}

/** ADR 0066: the old six ids are errors wherever a stat id goes (effect resist and derived formulas, condition statMods). */
function oldStatIds(b: Bundle, add: Add) {
  (b.statusEffects ?? []).forEach((raw, i) => {
    if (raw == null) return;
    const e = effectAsParsed(raw);
    const id = e.id ?? '';
    const resist = e.resist?.stat;
    if (e.resist != null && isOldStat(resist))
      add(
        'statusEffects',
        i,
        'resist.stat',
        `Effect '${id}' resist uses the old stat id '${resist}'${legacyHint(resist)}.`,
      );
    const derived = e.derived?.stat;
    if (e.derived != null && isOldStat(derived))
      add(
        'statusEffects',
        i,
        'derived.stat',
        `Effect '${id}' derived uses the old stat id '${derived}'${legacyHint(derived)}.`,
      );
  });
  (b.conditions?.types ?? []).forEach((c, i) => {
    if (c?.statMods == null) return;
    for (const k of Object.keys(c.statMods))
      if (isOldStat(k))
        add(
          'conditions',
          undefined,
          `types.${i}.statMods`,
          `Condition '${c.id ?? ''}' statMods uses the old stat id '${k}'${legacyHint(k)}.`,
        );
  });
}

/** Mirrors `ContentValidator.StatsProductionProblems` (the API refuses these only when publishing in Production). */
export function skillsProductionProblems(b: Bundle): string[] {
  const r = b.skillRules == null ? null : skillRulesAsParsed(b.skillRules);
  return r && num(r.devPointsMult) !== 1
    ? ['skillRules devPointsMult must be 1 in production.']
    : [];
}
